import { createSelector, createSlice } from "@reduxjs/toolkit";

import {
  fetchCropsServiceDataTableFilter,
  fetchSBActionsNames,
  fetchSBSites,
  fetchSBUserNames,
} from "../../actions/dataTable/DataFiltersAsyncActions";
import { RootState } from "../../../configureStore";
import { logOut } from "../layout";

export interface ValuesSelected {
  userFacingNameTextId?: number;
  name: string;
  id: string | number | undefined;
  cerealType?: string[];
}

export interface ValuesSelectedAutoComplete {
  name: string;
  id: number;
  isSelected: boolean;
  textId?: number;
}

export interface DataSBFiltersValues {
  locations: ValuesSelected[];
  actions: ValuesSelected[];
  users?: ValuesSelected[];
}

export interface TableFilters {
  column: string;
  operator: string | undefined;
  values: string | string[];
  value: string | string[];
}

export interface DataFiltersState {
  dataDateActivity: Date[];
  dateRangePickerOpen: boolean;
  speciesList: ValuesSelectedAutoComplete[];
  locations: {
    valuesSelected: ValuesSelected[];
    allValuesSelected: boolean;
  };
  actions: {
    valuesSelected: ValuesSelected[];
    allValuesSelected: boolean;
  };
  users: {
    valuesSelected: ValuesSelected[];
    allValuesSelected: boolean;
  };
  filtersSpecies: number[] | undefined;
  filtersLocation: string[] | undefined;
  filterActions: string[] | undefined;
  filterUsers: string[] | undefined;
  filterDate1: TableFilters | undefined;
  filterDate2: TableFilters | undefined;
  filtersSelected: number;
  dataSBFilterValues: DataSBFiltersValues;
  loadingFilters: boolean;
  errorFilters: string;
}

export const initialState: DataFiltersState = {
  dataDateActivity: [
    new Date(new Date().setDate(new Date().getDate() - 7)),
    new Date(new Date().setDate(new Date().getDate())),
  ],
  dateRangePickerOpen: false,
  speciesList: [],
  filtersSpecies: [],
  filtersLocation: [],
  filterActions: [],
  filterUsers: [],
  filterDate1: undefined,
  filterDate2: undefined,
  filtersSelected: 0,

  locations: { valuesSelected: [], allValuesSelected: false },
  actions: {
    valuesSelected: [],
    allValuesSelected: false,
  },
  users: {
    valuesSelected: [],
    allValuesSelected: false,
  },

  dataSBFilterValues: {
    locations: [{ name: "", id: undefined }],
    actions: [
      {
        name: "",
        id: undefined,
        cerealType: [],
        userFacingNameTextId: undefined,
      },
    ],
    users: [{ name: "", id: undefined }],
  },

  loadingFilters: false,
  errorFilters: "",
};

export const localeData = createSelector(
  (state: RootState) => state.LayoutReducer.locale,

  (locale) => {
    return locale;
  }
);

export const DataFiltersSlice = createSlice({
  name: "DataFiltersReducer",
  initialState,
  reducers: {
    setFiltersSelected(state, action) {
      return {
        ...state,
        filtersSelected: action.payload,
      };
    },
    setDateRangePicker(state, action) {
      return {
        ...state,
        dataDateActivity: action.payload,
      };
    },
    setOpenDateRangePicker(state, action) {
      return {
        ...state,
        dateRangePickerOpen: action.payload,
      };
    },
    setSelectSites(state, action) {
      const values = action?.payload.map((l) => {
        return l.name;
      });

      return {
        ...state,
        filtersLocation: values,
        locations: {
          ...state.locations,
          valuesSelected: action.payload,
        },
      };
    },
    setSelectAllSitesFilter(state, action) {
      const values = action?.payload.map((l) => {
        return l.name;
      });
      return {
        ...state,
        filtersLocation: values,
        locations: {
          allValuesSelected: true,
          valuesSelected: action.payload,
        },
      };
    },
    resetSelectAllSitesFilter(state) {
      return {
        ...state,
        locations: {
          allValuesSelected: false,
          valuesSelected: [],
        },
      };
    },

    setSelectUsers(state, action) {
      const values = action?.payload.map((l) => {
        return l.name;
      });
      return {
        ...state,
        filterUsers: values,
        users: {
          ...state.users,
          valuesSelected: action.payload,
        },
      };
    },
    setSelectAllUsersFilter(state, action) {
      const values = action?.payload.map((l) => {
        return l.name;
      });
      return {
        ...state,
        filterUsers: values,
        users: {
          allValuesSelected: true,
          valuesSelected: action.payload,
        },
      };
    },
    resetSelectAllUsersFilter(state) {
      return {
        ...state,
        users: {
          allValuesSelected: false,
          valuesSelected: [],
        },
      };
    },

    setSelectActions(state, action) {
      const values = action?.payload.map((l) => {
        return l.id;
      });
      return {
        ...state,
        filterActions: values,
        actions: {
          ...state.actions,
          valuesSelected: action.payload,
        },
      };
    },
    setSelectAllActionsFilter(state, action) {
      const values = action?.payload.map((l) => {
        return l.id;
      });

      state.filterActions = values;
      state.actions = {
        allValuesSelected: true,
        valuesSelected: action.payload,
      };
    },
    resetSelectAllActionsFilter(state) {
      return {
        ...state,
        actions: {
          allValuesSelected: false,
          valuesSelected: [],
        },
      };
    },

    changeStatusSpeciesFilter(state, action) {
      const newArr = state.speciesList.map((el) => {
        if (el.id === action.payload) {
          return { ...el, isSelected: !el.isSelected };
        } else return { ...el, isSelected: el.isSelected };
      });

      const selectedTextId = newArr
        .filter((v) => v.isSelected === true)
        .map((x) => x.textId)
        .filter((id): id is number => id !== undefined);

      state.speciesList = newArr;
      state.filtersSpecies = selectedTextId;
    },

    resetDataTableFilters(state) {
      const speciesReset = state.speciesList.map((el) => {
        return { ...el, isSelected: false };
      });

      return {
        ...state,
        //dataDateActivity: initialState.dataDateActivity,
        dateRangePickerOpen: false,
        speciesList: speciesReset,
        filtersSpecies: undefined,
        filtersLocation: undefined,
        filterActions: undefined,
        filterUsers: undefined,
        filterDate1: undefined,
        filterDate2: undefined,
        filtersSelected: 0,
        locations: { valuesSelected: [], allValuesSelected: false },
        actions: {
          valuesSelected: [],
          allValuesSelected: false,
        },
        users: {
          valuesSelected: [],
          allValuesSelected: false,
        },
      };
    },

    resetDataTable() {
      return initialState;
    },
  },

  extraReducers(builder) {
    builder
      .addCase(fetchCropsServiceDataTableFilter.pending, (state) => {
        state.loadingFilters = true;
        state.errorFilters = "";
      })
      .addCase(fetchCropsServiceDataTableFilter.fulfilled, (state, action) => {
        state.loadingFilters = false;
        state.speciesList = action.payload;
      })
      .addCase(fetchCropsServiceDataTableFilter.rejected, (state, action) => {
        state.loadingFilters = false;
        state.speciesList = [];
        state.errorFilters = action.payload as string;
      });

    builder
      .addCase(fetchSBSites.pending, (state) => {
        state.loadingFilters = true;
        state.errorFilters = "";
      })
      .addCase(fetchSBSites.fulfilled, (state, action) => {
        state.loadingFilters = false;
        state.dataSBFilterValues.locations = action.payload;
      })
      .addCase(fetchSBSites.rejected, (state, action) => {
        state.loadingFilters = false;
        state.dataSBFilterValues.locations =
          initialState.dataSBFilterValues.locations;
        state.errorFilters = action.payload as string;
      })

      .addCase(fetchSBUserNames.pending, (state) => {
        state.loadingFilters = true;
        state.errorFilters = "";
      })
      .addCase(fetchSBUserNames.fulfilled, (state, action) => {
        state.loadingFilters = false;
        state.dataSBFilterValues.users = action.payload;
      })
      .addCase(fetchSBUserNames.rejected, (state, action) => {
        state.loadingFilters = false;
        state.dataSBFilterValues.users = initialState.dataSBFilterValues.users;
        state.errorFilters = action.payload as string;
      })

      .addCase(fetchSBActionsNames.pending, (state) => {
        state.loadingFilters = true;
        state.errorFilters = "";
      })
      .addCase(fetchSBActionsNames.fulfilled, (state, action) => {
        state.loadingFilters = false;
        state.dataSBFilterValues.actions = action.payload;
      })
      .addCase(fetchSBActionsNames.rejected, (state, action) => {
        state.loadingFilters = false;
        state.dataSBFilterValues.actions =
          initialState.dataSBFilterValues.actions;
        state.errorFilters = action.payload as string;
      });

    builder.addCase(logOut, () => {
      return initialState;
    });
  },
});

export const {
  resetDataTable,
  setFiltersSelected,
  setDateRangePicker,
  setOpenDateRangePicker,
  resetSelectAllActionsFilter,
  setSelectActions,
  setSelectAllActionsFilter,
  setSelectSites,
  changeStatusSpeciesFilter,
  resetDataTableFilters,
  setSelectAllSitesFilter,
  resetSelectAllSitesFilter,
  resetSelectAllUsersFilter,
  setSelectAllUsersFilter,
  setSelectUsers,
} = DataFiltersSlice.actions;
export const DataFiltersReducer = DataFiltersSlice.reducer;
